import { ReactNode, useEffect, useRef, useState } from "react";
import {
  Tabs as TabsNextUi,
  Tab as TabNextUi,
  TabsProps as TabsPropsNextUi,
} from "@nextui-org/react";

import styles from "./TabsUnderline.module.scss";

interface Item {
  label: ReactNode;
  value: string;
}

interface Props extends TabsPropsNextUi {
  items: Item[];
  optionTab: string;
  setOptionTab: (val: string) => void;
  sticky?: boolean;
  endContent?: ReactNode;
  withEndContent?: boolean;
}

const TabsUnderline = ({
  items,
  optionTab,
  setOptionTab,
  sticky = false,
  endContent,
  withEndContent = false,
  ...rest
}: Props) => {
  const { tabContainer, stickyFixed, tabsWithEndContent } = styles;

  const sentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFixed, setIsFixed] = useState(false);
  const [spacerHeight, setSpacerHeight] = useState(0);

  useEffect(() => {
    if (!sticky || !sentinelRef.current || !containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFixed(
          !entry.isIntersecting && entry.boundingClientRect.top < 0,
        );
      },
      { threshold: 0, rootMargin: "12px 0px 0px 0px" },
    );

    observer.observe(sentinelRef.current);
    setSpacerHeight(containerRef.current.offsetHeight);

    return () => observer.disconnect();
  }, [sticky]);

  const tabs = (
    <div className={withEndContent ? tabsWithEndContent : undefined}>
      <TabsNextUi
        {...rest}
        onSelectionChange={(val) => {
          setOptionTab(val as string);
        }}
        selectedKey={optionTab}
        className={tabContainer}
        variant="underlined"
      >
        {items.map((tab) => (
          <TabNextUi key={tab.value} title={tab.label} />
        ))}
      </TabsNextUi>
      {endContent}
    </div>
  );

  if (!sticky) return tabs;

  return (
    <>
      <div ref={sentinelRef} />
      {isFixed && <div style={{ height: spacerHeight }} />}
      <div ref={containerRef} className={isFixed ? stickyFixed : undefined}>
        {tabs}
      </div>
    </>
  );
};

export default TabsUnderline;